<template>
  <div class="content statement-template-detail fixContent">
    <!-- 基本信息 -->
    <a-card :title="$t('基本信息')" class="mb16">
      <a-descriptions :column="4" size="small" bordered>
        <a-descriptions-item :label="$t('模板编码')">{{ detail.code || '-' }}</a-descriptions-item>
        <a-descriptions-item :label="$t('模板名称')">{{ detail.name || '-' }}</a-descriptions-item>
        <a-descriptions-item :label="$t('结算组织')">
          {{ detail.business_unit_name || '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('报表类型')">
          <a-tag
            :color="
              detail.type === 'INCOME' ? 'green' : detail.type === 'CASH_FLOW' ? 'cyan' : 'blue'
            "
          >
            {{ reportTypeMap[detail.type] || detail.type || '-' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('是否预置')">
          <a-tag :color="detail.is_preset === 1 ? 'purple' : 'gray'">
            {{ detail.is_preset === 1 ? $t('系统预置') : $t('自定义') }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('创建时间')">
          {{ detail.created_at || '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('更新时间')">
          {{ detail.updated_at || '-' }}
        </a-descriptions-item>
      </a-descriptions>
    </a-card>

    <!-- 报表项目列表 -->
    <a-card :title="$t('报表项目明细')" class="mb72">
      <template slot="extra">
        <a-button type="primary" size="small" icon="plus" @click="openAddItemModal">
          {{ $t('添加报表项目') }}
        </a-button>
      </template>

      <a-table
        :columns="detailColumns"
        :data-source="detailList"
        :pagination="false"
        row-key="id"
        size="small"
        bordered
        :loading="detailLoading"
      >
        <!-- 项目内容 with indentation by level -->
        <template slot="content" slot-scope="text, record">
          <span :style="{ paddingLeft: (record.level - 1) * 20 + 'px', display: 'inline-block' }">
            <a-tag v-if="record.level === 1" color="blue" size="small" style="margin-right: 6px">
              一级
            </a-tag>
            <a-tag
              v-else-if="record.level === 2"
              color="cyan"
              size="small"
              style="margin-right: 6px"
            >
              二级
            </a-tag>
            <a-tag v-else color="orange" size="small" style="margin-right: 6px">三级</a-tag>
            {{ text }}
          </span>
        </template>

        <!-- 取数来源 -->
        <template slot="express_type" slot-scope="text">
          <a-tag v-if="text === 'ACCOUNT'" color="green">{{ $t('科目') }}</a-tag>
          <a-tag v-else-if="text === 'SELF'" color="purple">{{ $t('表内') }}</a-tag>
          <a-tag v-else-if="text === 'CASH_FLOW_ITEM'" color="cyan">{{ $t('现金流项') }}</a-tag>
          <span v-else>-</span>
        </template>

        <!-- 操作 -->
        <template slot="action" slot-scope="text, record, index">
          <a-button
            type="link"
            size="small"
            icon="arrow-up"
            :disabled="index === 0"
            @click="moveUp(index)"
          />
          <a-button
            type="link"
            size="small"
            icon="arrow-down"
            :disabled="index === detailList.length - 1"
            @click="moveDown(index)"
          />
          <a-divider type="vertical" />
          <a-popover placement="topRight" trigger="click" :title="$t('移动至指定序号')">
            <template slot="content">
              <div style="display: flex; gap: 8px; align-items: center">
                <a-input-number
                  v-model="record.targetSort"
                  :min="1"
                  :max="detailList.length"
                  :precision="0"
                  size="small"
                  style="width: 80px"
                  :placeholder="$t('序号')"
                />
                <a-button type="primary" size="small" @click="quickMove(index, record.targetSort)">
                  {{ $t('确定') }}
                </a-button>
              </div>
            </template>
            <a-button type="link" size="small" icon="swap" />
          </a-popover>
          <a-divider type="vertical" />
          <a-popconfirm :title="$t('确定删除该报表项目吗？')" @confirm="removeItem(index)">
            <a class="text-danger">{{ $t('删除') }}</a>
          </a-popconfirm>
        </template>
      </a-table>
    </a-card>

    <!-- 添加报表项目弹窗 -->
    <a-modal
      :title="$t('选择报表项目')"
      :visible="addItemModalVisible"
      width="900px"
      @cancel="addItemModalVisible = false"
      @ok="confirmAddItems"
      :confirmLoading="addItemConfirmLoading"
    >
      <!-- 搜索区 -->
      <div style="margin-bottom: 12px; display: flex; gap: 12px; align-items: center">
        <CommonSelect
          :list="itemTypeList"
          :placeholder="$t('项目类型')"
          :code.sync="itemQueryParam.item_type_code"
          style="width: 180px"
          @change="searchItems"
        />
        <a-input
          v-model="itemQueryParam.name_or_code"
          :placeholder="$t('请输入编码/名称')"
          allowClear
          style="width: 200px"
          @pressEnter="searchItems"
        />
        <a-button type="primary" size="small" @click="searchItems">{{ $t('查询') }}</a-button>
        <a-button size="small" @click="resetItemQuery">{{ $t('重置') }}</a-button>
        <div style="flex: 1"></div>
        <div style="display: flex; gap: 8px; align-items: center">
          <span style="font-size: 13px; color: rgba(0, 0, 0, 0.65)">{{ $t('插入序号') }}:</span>
          <a-input-number
            v-model="insertSort"
            :placeholder="$t('默认塞到最后')"
            :min="1"
            :precision="0"
            style="width: 120px"
          />
        </div>
      </div>

      <a-table
        :columns="itemModalColumns"
        :data-source="modalItemList"
        :loading="itemModalLoading"
        :pagination="false"
        :row-selection="{
          selectedRowKeys: selectedItemKeys,
          onChange: onItemSelectChange,
          getCheckboxProps: (record) => ({ props: { disabled: isAlreadyAdded(record) } }),
        }"
        row-key="id"
        size="small"
        bordered
        :scroll="{ y: 360 }"
      >
        <template slot="express_type" slot-scope="text">
          <a-tag v-if="text === 'ACCOUNT'" color="green">{{ $t('科目') }}</a-tag>
          <a-tag v-else-if="text === 'SELF'" color="purple">{{ $t('表内') }}</a-tag>
          <a-tag v-else-if="text === 'CASH_FLOW_ITEM'" color="cyan">{{ $t('现金流项') }}</a-tag>
          <span v-else>-</span>
        </template>
      </a-table>

      <!-- 弹窗分页 -->
      <div style="margin-top: 10px; display: flex; justify-content: flex-end">
        <pagination
          :pageNo="itemPagination.current"
          :length="itemPagination.pageSize"
          :total-count="itemPagination.total"
          @paginate="itemPaginate"
        />
      </div>
    </a-modal>

    <!-- 固定底部操作栏 -->
    <div class="footer">
      <a-button @click="$router.back()">{{ $t('返回') }}</a-button>
      <a-button type="primary" :loading="saveLoading" @click="handleSave" style="margin-left: 12px">
        {{ $t('保存') }}
      </a-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import http from '@/service/axios'
import api from '@/service/api'
import pagination from '@/component/pagination.vue'

export default {
  name: 'StatementTemplateDetail',
  components: { pagination },
  data() {
    return {
      id: this.$route.query.id,
      detailLoading: false,
      saveLoading: false,
      detail: {},
      detailList: [],

      reportTypeMap: {
        INCOME: this.$t('利润表'),
        CASH_FLOW: this.$t('现金流量表'),
        BALANCE_SHEET: this.$t('资产负债表'),
      },

      detailColumns: [
        {
          title: this.$t('排序'),
          dataIndex: 'sort',
          width: 60,
          align: 'center',
        },
        {
          title: this.$t('项目编码'),
          dataIndex: 'statement_item_code',
          width: 120,
        },
        {
          title: this.$t('项目内容'),
          dataIndex: 'content',
          scopedSlots: { customRender: 'content' },
          width: 300,
        },
        {
          title: this.$t('项目名称'),
          dataIndex: 'statement_item_name',
          width: 200,
        },
        {
          title: this.$t('项目类型'),
          dataIndex: 'statement_item_type_name',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('取数来源'),
          dataIndex: 'express_type',
          scopedSlots: { customRender: 'express_type' },
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('表达式'),
          dataIndex: 'express',
          width: 260,
          ellipsis: true,
        },
        {
          title: this.$t('层级'),
          dataIndex: 'level',
          width: 60,
          align: 'center',
        },
        {
          title: this.$t('操作'),
          key: 'action',
          width: 160,
          align: 'center',
          scopedSlots: { customRender: 'action' },
          fixed: 'right',
        },
      ],

      // 添加项目弹窗
      addItemModalVisible: false,
      addItemConfirmLoading: false,
      itemModalLoading: false,
      insertSort: undefined,
      itemTypeList: [],
      modalItemList: [],
      selectedItemKeys: [],
      selectedItems: [],
      itemQueryParam: {
        item_type_code: undefined,
        name_or_code: undefined,
      },
      itemPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },

      itemModalColumns: [
        { title: this.$t('项目编码'), dataIndex: 'code', width: 140 },
        { title: this.$t('项目名称'), dataIndex: 'name', width: 180 },
        { title: this.$t('项目类型'), dataIndex: 'item_type_name', width: 120 },
        {
          title: this.$t('取数来源'),
          dataIndex: 'express_type',
          scopedSlots: { customRender: 'express_type' },
          width: 100,
          align: 'center',
        },
        { title: this.$t('层级'), dataIndex: 'level', width: 70, align: 'center' },
        { title: this.$t('表达式'), dataIndex: 'express', ellipsis: true },
      ],
    }
  },
  computed: {
    ...mapState(['settlementBuList']),
  },
  created() {
    if (this.id) {
      this.fetchDetail()
    }
    this.loadItemTypeList()
  },
  methods: {
    async fetchDetail() {
      this.detailLoading = true
      try {
        const res = await http({
          url: api.getFinancialStatementTemplateDetail,
          data: { id: this.id },
        })
        if (res && res.result) {
          const data = res.result
          this.detail = data
          this.detailList = (data.detail_list || []).map((item, idx) => ({
            ...item,
            sort: idx + 1,
            targetSort: undefined,
          }))
        }
      } catch (err) {
        this.$message.error(this.$t('获取详情失败'))
      } finally {
        this.detailLoading = false
      }
    },

    async loadItemTypeList() {
      try {
        await http({
          url: api.getFinancialStatementItemTypeList,
          type: 'post',
          data: {},
          success: (res) => {
            if (res.success) {
              this.itemTypeList = (res.result || []).map((item) => ({
                code: item.code,
                name: item.name,
              }))
            }
          },
        })
      } catch (err) {
        console.error('load item type list error', err)
      }
    },

    // ========== 排序操作 ==========
    moveUp(index) {
      if (index === 0) return
      const list = [...this.detailList]
      ;[list[index - 1], list[index]] = [list[index], list[index - 1]]
      this.detailList = list.map((item, idx) => ({ ...item, sort: idx + 1 }))
    },

    moveDown(index) {
      if (index === this.detailList.length - 1) return
      const list = [...this.detailList]
      ;[list[index], list[index + 1]] = [list[index + 1], list[index]]
      this.detailList = list.map((item, idx) => ({ ...item, sort: idx + 1 }))
    },

    // ========== 删除项目 ==========
    removeItem(index) {
      const list = [...this.detailList]
      list.splice(index, 1)
      this.detailList = list.map((item, idx) => ({ ...item, sort: idx + 1 }))
    },

    quickMove(index, targetSort) {
      if (targetSort === undefined || targetSort === null || isNaN(targetSort)) {
        this.$message.warning(this.$t('请输入有效的序号'))
        return
      }
      const toIndex = Math.max(0, Math.min(this.detailList.length - 1, targetSort - 1))
      if (toIndex === index) {
        return
      }
      const list = [...this.detailList]
      const [item] = list.splice(index, 1)
      list.splice(toIndex, 0, item)
      this.detailList = list.map((item, idx) => ({
        ...item,
        sort: idx + 1,
        targetSort: undefined,
      }))
    },

    // ========== 添加项目弹窗 ==========
    openAddItemModal() {
      this.addItemModalVisible = true
      this.selectedItemKeys = []
      this.selectedItems = []
      this.insertSort = undefined
      this.itemQueryParam = { item_type_code: undefined, name_or_code: undefined }
      this.itemPagination.current = 1
      this.loadModalItems()
    },

    async loadModalItems() {
      this.itemModalLoading = true
      try {
        await http({
          url: api.getFinancialStatementItemList,
          type: 'post',
          data: {
            item_type_code: this.itemQueryParam.item_type_code,
            name_or_code: this.itemQueryParam.name_or_code,
            page_no: this.itemPagination.current,
            page_size: this.itemPagination.pageSize,
          },
          success: (res) => {
            if (res.success && res.result) {
              this.modalItemList = res.result.list || []
              this.itemPagination.total = res.result.total || 0
            }
          },
        })
      } catch (err) {
        console.error('load modal items error', err)
      } finally {
        this.itemModalLoading = false
      }
    },

    searchItems() {
      this.itemPagination.current = 1
      this.loadModalItems()
    },

    resetItemQuery() {
      this.itemQueryParam = { item_type_code: undefined, name_or_code: undefined }
      this.itemPagination.current = 1
      this.loadModalItems()
    },

    itemPaginate(data) {
      this.itemPagination.current = data.pageNo
      this.itemPagination.pageSize = data.pageSize
      this.loadModalItems()
    },

    isAlreadyAdded(record) {
      return this.detailList.some((item) => item.statement_item_code === record.code)
    },

    onItemSelectChange(selectedRowKeys, selectedRows) {
      this.selectedItemKeys = selectedRowKeys
      this.selectedItems = selectedRows
    },

    confirmAddItems() {
      if (this.selectedItems.length === 0) {
        this.$message.warning(this.$t('请选择至少一个报表项目'))
        return
      }
      const existingCodes = new Set(this.detailList.map((item) => item.statement_item_code))
      const newItems = this.selectedItems
        .filter((item) => !existingCodes.has(item.code))
        .map((item) => ({
          id: undefined,
          business_unit_code: this.detail.business_unit_code,
          business_unit_name: this.detail.business_unit_name,
          template_code: this.detail.code,
          statement_item_code: item.code,
          statement_item_name: item.name,
          statement_item_type_code: item.item_type_code,
          statement_item_type_name: item.item_type_name,
          content: item.display_content || item.name,
          express: item.express || '',
          express_type: item.express_type || '',
          level: item.level || 1,
          merchant_code: this.detail.merchant_code,
          sort: 0,
          targetSort: undefined,
        }))

      if (newItems.length > 0) {
        let combined = []
        if (this.insertSort !== undefined && this.insertSort !== null && this.insertSort > 0) {
          const insertIndex = Math.min(this.detailList.length, this.insertSort - 1)
          const list = [...this.detailList]
          list.splice(insertIndex, 0, ...newItems)
          combined = list
        } else {
          combined = [...this.detailList, ...newItems]
        }
        this.detailList = combined.map((item, idx) => ({
          ...item,
          sort: idx + 1,
        }))
      }
      this.addItemModalVisible = false
      this.selectedItemKeys = []
      this.selectedItems = []
    },

    // ========== 保存 ==========
    async handleSave() {
      this.saveLoading = true
      try {
        await http({
          url: api.createOrUpdateFinancialStatementTemplate,
          type: 'post',
          data: {
            id: this.id ? Number(this.id) : undefined,
            business_unit_code: this.detail.business_unit_code,
            type: this.detail.type,
            code: this.detail.code,
            name: this.detail.name,
            statement_item_code_list: this.detailList.map((item) => item.statement_item_code),
          },
          success: (res) => {
            if (res.success) {
              this.$message.success(this.$t('保存成功'))
              this.fetchDetail()
            } else {
              this.$message.error(res.msg || this.$t('保存失败'))
            }
          },
        })
      } catch (err) {
        this.$message.error(this.$t('保存发生错误'))
      } finally {
        this.saveLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.statement-template-detail {
  padding: 10px 0 80px 0;
  background: #f0f2f5;
  min-height: calc(100vh - 150px);

  .mb16 {
    margin-bottom: 16px;
  }

  .mb72 {
    margin-bottom: 72px;
  }

  .footer {
    position: fixed;
    bottom: 0;
    left: 185px;
    right: 0;
    height: 56px;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 24px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
    z-index: 1000;
    transition: left 0.2s;
  }

  .text-danger {
    color: #ff4d4f;
  }

  :deep(.ant-card-head-title) {
    font-weight: 600;
  }

  :deep(.ant-descriptions-item-label) {
    font-weight: 500;
    background: #fafafa;
  }
}
</style>
